import { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { Dictionary } from 'lodash';
import { get, intersectionWith, isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ExclusiveIcon } from '@swf/casino';
import type { CasinoTable, Jackpot } from '@swf/core/types/CasinoGame';
import { MarketingTag, OverlayHierarchy, PlayType } from '@swf/core/types/CasinoGame';

import JackpotAmount from '../JackpotAmount/JackpotAmount';

import { TXTMOB_Exclusive } from './Overlay.messages.json';
import styles from './Overlay.scss';

export interface Props {
  className?: string;
  playMoney?: boolean;
  jackpot?: Jackpot;
  visible: boolean;
  tags?: string[];
  tables?: Dictionary<CasinoTable[]>;
  overlayHierarchy?: string;
  setIsOverlay: (arg1: boolean) => void;
}

type OverlayType = 'jackpot' | 'exclusive';

const tagI18nByPriority = {
  [MarketingTag.Exclusive]: TXTMOB_Exclusive
};

const Overlay: React.FC<Props> = ({
  className,
  jackpot,
  visible,
  tags = [],
  tables,
  overlayHierarchy,
  setIsOverlay
}) => {
  const { formatMessage } = useIntl();
  const [tag] = intersectionWith(Object.keys(tagI18nByPriority), tags, (a, b) => b.startsWith(a));
  const playMoney = !!get(tables, [PlayType.Single, 0, 'playMoney']);
  const isOverlay = useMemo(() => !isEmpty(jackpot) || !isEmpty(tag), [jackpot, tag]);

  useEffect(() => {
    if (isOverlay) setIsOverlay(true);
  }, [isOverlay, setIsOverlay]);

  if (!isOverlay) return null;

  const overlayType: OverlayType =
    (overlayHierarchy === OverlayHierarchy.Jackpot && jackpot) ||
    (overlayHierarchy === OverlayHierarchy.Exclusive && jackpot && isEmpty(tag))
      ? 'jackpot'
      : 'exclusive';
  const getContent = () => {
    // check for jackpot again to please TypeScript
    if (overlayType === 'jackpot' && jackpot) {
      return (
        <JackpotAmount
          jackpot={jackpot}
          currency={playMoney ? Currency.PlayMoney : jackpot.currency}
          visible={visible}
        />
      );
    }

    return (
      <>
        <ExclusiveIcon alt={formatMessage(TXTMOB_Exclusive)} className={styles.exclusiveIcon} />
        <Translation {...tagI18nByPriority[MarketingTag.Exclusive]} />
      </>
    );
  };

  return (
    <div
      className={classNames(className, styles.jackpot)}
      data-testid={`game-tile-overlay-${overlayType}`}
    >
      {getContent()}
    </div>
  );
};

export default Overlay;
